import * as React from 'react'
import Image from 'next/image'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSearchBox } from 'react-instantsearch'
import debounce from 'lodash.debounce'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { SearchIcon } from 'primitives/icons'
import { Link } from '@repo/web-ui/components/link'
import { IconAutoFixHigh } from '@repo/design-system-cf/components/icons'
import { facebookEvent, FacebookEventName } from '@repo/analytics/facebook'
import { useTranslations } from 'next-intl'
import { useSiteData } from '@repo/web-ui/hooks/use-site-data'
import { useFeatureIsOn } from 'growthbook'
import { LFS_URL } from '@web/utils/env'

export function SearchInput() {
  const searchParams = useSearchParams()
  const isAiSearchEnabled = useFeatureIsOn('grow-2437-ai-search-experiment')
  const pathname = usePathname()
  const t = useTranslations('pages.search')
  const { refine, query } = useSearchBox()

  const { data: siteData } = useSiteData()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const isBannerDisplayed =
    siteData?.allAccessYearlyBanner ||
    siteData?.allAccessYearlyTrialBanner ||
    siteData?.allAccessYearlyExtendBanner

  function sendEvent() {
    let bannerDisplayed = false

    if (isBannerDisplayed) {
      bannerDisplayed = true
    }

    sendAmplitudeEvent(AmplitudeEvents.PRODUCTS_SEARCHED, {
      query: inputRef.current?.value ?? '',
      source: 'search-page',
      banner_above_nav: bannerDisplayed ? isBannerDisplayed?.bannerAboveNav || '' : '',
      banner_above_nav_state: bannerDisplayed ? isBannerDisplayed?.bannerAboveNavState || '' : '',
    })

    facebookEvent(FacebookEventName.SEARCH, {
      search_string: inputRef.current?.value ?? '',
    })
  }

  const handleSearch = React.useCallback(function handleSearch() {
    refine(inputRef.current?.value ?? '')
    sendEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      refine(inputRef.current?.value ?? '')
    }
  }

  // TODO check if this is still needed
  React.useEffect(() => {
    let oldQuery = searchParams.get('query') || ''
    const handleRouteChange = (url: string) => {
      const urlParams = new URLSearchParams(new URL(url, window.location.origin).search)
      const newQuery = urlParams.get('query') || ''

      if (oldQuery !== newQuery) {
        inputRef.current?.focus()
        oldQuery = newQuery
      }
    }

    handleRouteChange(`${pathname}?${searchParams.toString()}`)
  }, [pathname, searchParams])

  const debouncedCallback = React.useMemo(() => {
    return debounce(handleSearch, 1000)
  }, [handleSearch])

  function handleChange() {
    debouncedCallback()
  }

  return (
    <div className="relative flex w-full flex-col">
      <div className="relative flex w-full items-center">
        <input
          data-testid="search-input-sidebar"
          ref={inputRef}
          onChange={handleChange}
          className="placeholder-transition h-[40px] w-full rounded-[6px] border border-gray-700 bg-gray-900 px-5 py-[10px] pr-10 text-gray-50 placeholder:text-gray-700 focus:outline-none"
          type="text"
          placeholder={t('placeholder')}
          onKeyPress={handleKeyPress}
          defaultValue={query}
        />

        <button
          type="button"
          onClick={handleSearch}
          className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-gray-200"
        >
          <SearchIcon aria-label="search" className="size-4" />
        </button>
      </div>

      {isAiSearchEnabled ? (
        <Link
          href={`https://search.creativefabrica.com${query ? `?search=${encodeURIComponent(query)}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cf-white bg-cf-black relative mt-3 flex w-full items-center gap-3 overflow-hidden rounded-[6px] px-4 py-3 transition-opacity hover:opacity-90"
        >
          <Image
            src={`${LFS_URL}/web/pages/search/spark-banner-cat-page-marketing.png`}
            alt=""
            unoptimized
            className="absolute inset-0 size-full object-center blur-sm rotate-180 scale-120"
            width={100}
            height={100}
          />
          <div className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-white/20">
            <IconAutoFixHigh className="size-4 text-white" />
          </div>
          <div className="relative flex flex-col gap-0.5">
            <span className="text-sm font-bold text-white">{t('ai_search_banner')}</span>
            <span className="text-xs text-white">{t('ai_search_banner_subtitle')}</span>
          </div>
        </Link>
      ) : null}
    </div>
  )
}
